import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form, Card, Button, Alert, Badge, Spinner } from "react-bootstrap";
import { Calendar2Check, ArrowClockwise, CheckCircle, XCircle, CreditCard, InfoCircle } from 'react-bootstrap-icons';
import { useLocation } from "react-router-dom";
import HeadingBanner from "../components/HeadingBanner";
import LeftPanel from "../components/LeftPanel";
import OrientationPrompt from "../components/OrientationPrompt";
import SchoolRollNumberInput from "../components/SchoolRollNumberInput";
import { getTrialBySchoolId, updateTrialBySchoolId, applyTrialExtension } from "../services/APIClient";

function TrialManagement() {
  const location = useLocation();
  const [isPortrait, setIsPortrait] = useState(
    window.matchMedia("(orientation: portrait)").matches
  );
  const [name, setName] = useState("User");
  const [schoolid, setSchoolid] = useState(null);
  const [schoolName, setSchoolName] = useState("");
  
  // For trialist data
  const [trialist, setTrialist] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [updateButtonDisabled, setUpdateButtonDisabled] = useState(true);

  // For form fields
  const [formData, setFormData] = useState({
    start_date: "",
    end_date: "",
    start_selected: false,
    extension_applied: false,
    expired: false,
    purchased: false,
    purchased_2425: false,
    purchased_2526: false,
    purchased_2627: false,
    purchased_2728: false,
    purchaser_name: ""
  });

  // For original form data (to detect changes)
  const [originalFormData, setOriginalFormData] = useState({});
  
  // For date formatting
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // For date formatting display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "Not set";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Calculate trial status
  const getTrialStatus = () => {
    if (!trialist) return null;
    
    const now = new Date();
    const startDate = trialist.start_date ? new Date(trialist.start_date) : null;
    const endDate = trialist.end_date ? new Date(trialist.end_date) : null;
    
    if (trialist.purchased || trialist.purchased_2425 || trialist.purchased_2526 || trialist.purchased_2627 || trialist.purchased_2728) {
      return { status: "Purchased", variant: "success", icon: <CreditCard className="me-1" /> };
    }
    if (trialist.expired) return { status: "Expired", variant: "danger", icon: <XCircle className="me-1" /> };
    if (!startDate || !endDate) return { status: "Not Started", variant: "secondary", icon: <InfoCircle className="me-1" /> };
    if (now > endDate) return { status: "Overdue", variant: "warning", icon: <ArrowClockwise className="me-1" /> };
    if (now < startDate) return { status: "Scheduled", variant: "primary", icon: <Calendar2Check className="me-1" /> };
    
    // Calculate days remaining
    const daysRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
    return { 
      status: `Active (${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'} remaining)`, 
      variant: daysRemaining < 3 ? "warning" : "success",
      icon: <Calendar2Check className="me-1" />
    };
  };

  // Check if form data has changed from original
  useEffect(() => {
    if (trialist) {
      let hasChanged = false;
      for (const key in formData) {
        if (formData[key] !== originalFormData[key]) {
          hasChanged = true;
          break;
        }
      }
      setUpdateButtonDisabled(!hasChanged);
    }
  }, [formData, originalFormData, trialist]);

  // Handle device orientation
  useEffect(() => {
    const handleOrientationChange = () => {
      setIsPortrait(window.innerWidth < window.innerHeight);
    };

    window.addEventListener("resize", handleOrientationChange);

    return () => {
      window.removeEventListener("resize", handleOrientationChange);
    };
  }, []);

  useEffect(() => {
    setName("User");
    const params = new URLSearchParams(location.search);
    const schoolIdFromQuery = params.get('schoolId');
    const schoolNameFromQuery = params.get('schoolName');
    if (schoolIdFromQuery) {
      setSchoolid(schoolIdFromQuery);
      if (schoolNameFromQuery) setSchoolName(decodeURIComponent(schoolNameFromQuery));
    }
  }, []);

  // Fetch trialist data when school ID is set
  useEffect(() => {
    if (schoolid) {
      fetchTrialistData();
    }
  }, [schoolid]);

  // Fetch trialist data from API
  const fetchTrialistData = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const response = await getTrialBySchoolId(schoolid);
      
      if (response && response.length > 0) {
        setTrialist(response[0]); // Assuming the first result is what we want
        
        // Format the data for the form
        const formattedData = {
          start_date: formatDateForInput(response[0].start_date),
          end_date: formatDateForInput(response[0].end_date),
          start_selected: response[0].start_selected || false,
          extension_applied: response[0].extension_applied || false,
          expired: response[0].expired || false,
          purchased: response[0].purchased || false,
          purchased_2425: response[0].purchased_2425 || false,
          purchased_2526: response[0].purchased_2526 || false,
          purchased_2627: response[0].purchased_2627 || false,
          purchased_2728: response[0].purchased_2728 || false,
          purchaser_name: response[0].purchaser_name || ""
        };
        
        // Update form data
        setFormData(formattedData);
        
        // Save original form data for comparison
        setOriginalFormData({ ...formattedData });
      } else {
        setTrialist(null);
        setError("No trial found for this school");
      }
    } catch (err) {
      console.error("Error fetching trialist data:", err);
      setError(`Error fetching trial data: ${err.response?.data?.error?.message || err.message}`);
      setTrialist(null);
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    const newFormData = {
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    };
    
    setFormData(newFormData);
  };

  // Reset form to original values
  const handleResetForm = () => {
    setFormData({ ...originalFormData });
    setError(null);
    setSuccess(null);
  };

  // Apply trial extension
  const handleApplyExtension = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      await applyTrialExtension(schoolid);
      setSuccess("Trial successfully extended by 7 days");
      // Refresh trialist data
      fetchTrialistData();
    } catch (err) {
      console.error("Error extending trial:", err);
      setError(`Error extending trial: ${err.response?.data?.error?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Update trialist data
  const handleUpdateTrialist = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    // Prepare updates object, only including fields that have changed
    const updates = {};
    Object.keys(formData).forEach(key => {
      // For dates, ensure they're in ISO format if provided
      if ((key === 'start_date' || key === 'end_date') && formData[key]) {
        updates[key] = new Date(formData[key]).toISOString();
      } 
      // For other fields, include them if they differ from original values
      else if (formData[key] !== originalFormData[key]) {
        updates[key] = formData[key];
      }
    });
    
    try {
      await updateTrialBySchoolId(schoolid, updates);
      setSuccess("Trial information successfully updated");
      // Refresh trialist data
      fetchTrialistData();
    } catch (err) {
      console.error("Error updating trialist:", err);
      setError(`Error updating trial information: ${err.response?.data?.error?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Container fluid className="vh-100">
        {/* HeadingBanner */}
        <HeadingBanner name={`Welcome ${name}!`} isLoggedIn={true} cloud={true} />

        <Row className="h-100">
          {/* LeftPanel */}
          <LeftPanel />

          {/* Main content */}
          <Col sm={{ span: 10, offset: 2 }} className="blue-background d-flex flex-column justify-content-start align-items-center">
            
            {/* Page Header */}
            <div className="w-75 mt-4 mb-3">
              <h2 className="text-white">Trial Management</h2>
              <p className="text-white">View and update trial information for schools</p>
            </div>
            
            {/* School Selection Section */}
            <div className="w-75 mt-2 bg-white rounded shadow-sm p-4">
              <h4 className="mb-3">School Selection</h4>
              <SchoolRollNumberInput
                onSchoolIdRetrieved={setSchoolid}
                schoolNameRetrieved={setSchoolName}
              />
              
              {schoolName && (
                <div className="mt-3 p-3 bg-light rounded d-flex justify-content-between align-items-center">
                  <div>
                    <strong className="me-2">Selected School:</strong> 
                    <span className="text-primary">{schoolName}</span>
                  </div>
                  {schoolid && (
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={() => fetchTrialistData()}
                      disabled={loading}
                    >
                      <ArrowClockwise className="me-1" /> Refresh
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Loading spinner */}
            {loading && (
              <div className="w-75 mt-4 text-center">
                <Spinner animation="border" role="status" variant="primary">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="w-75 mt-4">
                <Alert variant="danger">
                  <strong>Error: </strong>{error}
                </Alert>
              </div>
            )}

            {/* Success message */}
            {success && (
              <div className="w-75 mt-4">
                <Alert variant="success">
                  <strong>Success: </strong>{success}
                </Alert>
              </div>
            )}

            {/* Trialist Information */}
            {trialist && (
              <div className="w-75 mt-4 mb-5">
                <Card className="shadow">
                  <Card.Header className="text-white d-flex justify-content-between align-items-center">
                    <h4 className="mb-0">Trial Information</h4>
                    {getTrialStatus() && (
                      <Badge pill bg={getTrialStatus().variant} className="d-flex align-items-center py-2 px-3">
                        {getTrialStatus().icon}
                        {getTrialStatus().status}
                      </Badge>
                    )}
                  </Card.Header>
                  <Card.Body>
                    <Row className="mb-4">
                      <Col md={4}>
                        <Card className="h-100 bg-light border-0">
                          <Card.Body>
                            <h5 className="card-title border-bottom pb-2 mb-3">Trial Details</h5>
                            <p className="mb-2">
                              <strong>Trial ID:</strong> {trialist.trialid}
                            </p>
                          </Card.Body>
                        </Card>
                      </Col>
                      
                      <Col md={4}>
                        <Card className="h-100 bg-light border-0">
                          <Card.Body>
                            <h5 className="card-title border-bottom pb-2 mb-3">Dates</h5>
                            <p className="mb-2">
                              <strong>Start Date:</strong> {formatDateForDisplay(trialist.start_date)}
                            </p>
                            <p className="mb-0">
                              <strong>End Date:</strong> {formatDateForDisplay(trialist.end_date)}
                            </p>
                          </Card.Body>
                        </Card>
                      </Col>
                      
                      <Col md={4}>
                        <Card className="h-100 bg-light border-0">
                          <Card.Body>
                            <h5 className="card-title border-bottom pb-2 mb-3">Status Flags</h5>
                            <div className="d-flex flex-column">
                              <Badge bg={trialist.start_selected ? "success" : "secondary"} className="mb-2 text-start">
                                Start Selected: {trialist.start_selected ? "Yes" : "No"}
                              </Badge>
                              <Badge bg={trialist.extension_applied ? "success" : "secondary"} className="mb-2 text-start">
                                Extension Applied: {trialist.extension_applied ? "Yes" : "No"}
                              </Badge>
                              <Badge bg={trialist.expired ? "danger" : "secondary"} className="mb-2 text-start">
                                Expired: {trialist.expired ? "Yes" : "No"}
                              </Badge>
                              <Badge bg={trialist.purchased ? "success" : "secondary"} className="text-start">
                                Purchased: {trialist.purchased ? "Yes" : "No"}
                                {trialist.purchased && trialist.purchaser_name && (
                                  <span> by {trialist.purchaser_name}</span>
                                )}
                              </Badge>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                    
                    <hr />
                    
                    <h5 className="mb-3">Update Trial Information</h5>
                    <Form>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Start Date</Form.Label>
                            <Form.Control
                              type="date"
                              name="start_date"
                              value={formData.start_date}
                              onChange={handleInputChange}
                              className={formData.start_date !== originalFormData.start_date ? "border-primary" : ""}
                            />
                            <Form.Text className="text-muted">
                              When the trial begins
                            </Form.Text>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>End Date</Form.Label>
                            <Form.Control
                              type="date"
                              name="end_date"
                              value={formData.end_date}
                              onChange={handleInputChange}
                              className={formData.end_date !== originalFormData.end_date ? "border-primary" : ""}
                            />
                            <Form.Text className="text-muted">
                              When the trial expires
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      </Row>
                      
                      <Row className="mt-3">
                        <Col md={4}>
                          <Card className="border-0 h-100">
                            <Card.Body className="px-0">
                              <Form.Group className="mb-0">
                                <Form.Check
                                  type="switch"
                                  id="start_selected"
                                  name="start_selected"
                                  label="Start Selected"
                                  checked={formData.start_selected}
                                  onChange={handleInputChange}
                                  className={formData.start_selected !== originalFormData.start_selected ? "text-primary fw-bold" : ""}
                                />
                              </Form.Group>
                            </Card.Body>
                          </Card>
                        </Col>
                        
                        <Col md={4}>
                          <Card className="border-0 h-100">
                            <Card.Body className="px-0">
                              <Form.Group className="mb-0">
                                <Form.Check
                                  type="switch"
                                  id="extension_applied"
                                  name="extension_applied"
                                  label="Extension Applied"
                                  checked={formData.extension_applied}
                                  onChange={handleInputChange}
                                  className={formData.extension_applied !== originalFormData.extension_applied ? "text-primary fw-bold" : ""}
                                />
                              </Form.Group>
                            </Card.Body>
                          </Card>
                        </Col>
                        
                        <Col md={4}>
                          <Card className="border-0 h-100">
                            <Card.Body className="px-0">
                              <Form.Group className="mb-0">
                                <Form.Check
                                  type="switch"
                                  id="expired"
                                  name="expired"
                                  label="Expired"
                                  checked={formData.expired}
                                  onChange={handleInputChange}
                                  className={formData.expired !== originalFormData.expired ? "text-primary fw-bold" : ""}
                                />
                              </Form.Group>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                      
                      {/* Purchase Information Section */}
                      <Row className="mt-4">
                        <Col md={8}>
                          <Card className="border-0 bg-light">
                            <Card.Body>
                              <h5 className="mb-3">Purchase Information</h5>
                              
                              {/* Legacy Purchase Field */}
                              <Form.Group className="mb-3">
                                <Form.Check
                                  type="switch"
                                  id="purchased"
                                  name="purchased"
                                  label={<span className="fw-bold">Purchased (Legacy)</span>}
                                  checked={formData.purchased}
                                  onChange={handleInputChange}
                                  className={formData.purchased !== originalFormData.purchased ? "text-primary fw-bold" : ""}
                                />
                              </Form.Group>
                              
                              <hr />
                              
                              {/* School Year Purchases */}
                              <h6 className="mb-3">School Year Purchases</h6>
                              
                              <Row>
                                <Col md={6}>
                                  <Form.Group className="mb-3">
                                    <Form.Check
                                      type="switch"
                                      id="purchased_2425"
                                      name="purchased_2425"
                                      label={<span className="fw-bold">2024-25 School Year</span>}
                                      checked={formData.purchased_2425}
                                      onChange={handleInputChange}
                                      className={formData.purchased_2425 !== originalFormData.purchased_2425 ? "text-primary fw-bold" : ""}
                                    />
                                  </Form.Group>
                                  
                                  <Form.Group className="mb-3">
                                    <Form.Check
                                      type="switch"
                                      id="purchased_2526"
                                      name="purchased_2526"
                                      label={<span className="fw-bold">2025-26 School Year</span>}
                                      checked={formData.purchased_2526}
                                      onChange={handleInputChange}
                                      className={formData.purchased_2526 !== originalFormData.purchased_2526 ? "text-primary fw-bold" : ""}
                                    />
                                  </Form.Group>
                                </Col>
                                
                                <Col md={6}>
                                  <Form.Group className="mb-3">
                                    <Form.Check
                                      type="switch"
                                      id="purchased_2627"
                                      name="purchased_2627"
                                      label={<span className="fw-bold">2026-27 School Year</span>}
                                      checked={formData.purchased_2627}
                                      onChange={handleInputChange}
                                      className={formData.purchased_2627 !== originalFormData.purchased_2627 ? "text-primary fw-bold" : ""}
                                    />
                                  </Form.Group>
                                  
                                  <Form.Group className="mb-3">
                                    <Form.Check
                                      type="switch"
                                      id="purchased_2728"
                                      name="purchased_2728"
                                      label={<span className="fw-bold">2027-28 School Year</span>}
                                      checked={formData.purchased_2728}
                                      onChange={handleInputChange}
                                      className={formData.purchased_2728 !== originalFormData.purchased_2728 ? "text-primary fw-bold" : ""}
                                    />
                                  </Form.Group>
                                </Col>
                              </Row>
                              
                              <hr />
                              
                              <Form.Group className="mb-0">
                                <Form.Label>Purchaser Name</Form.Label>
                                <div className="custom-input-wrapper">
                                  <input
                                    type="text"
                                    name="purchaser_name"
                                    value={formData.purchaser_name}
                                    onChange={(e) => setFormData({...formData, purchaser_name: e.target.value})}
                                    placeholder="Enter purchaser name"
                                    disabled={!formData.purchased && !formData.purchased_2425 && !formData.purchased_2526 && !formData.purchased_2627 && !formData.purchased_2728}
                                    className={`form-control ${formData.purchaser_name !== originalFormData.purchaser_name ? "border-primary" : ""}`}
                                    style={{ width: '100%', padding: '0.375rem 0.75rem' }}
                                  />
                                </div>
                                <Form.Text className="text-muted">
                                  Required when marking any year as purchased
                                </Form.Text>
                              </Form.Group>
                            </Card.Body>
                          </Card>
                        </Col>
                        
                        <Col md={4} className="d-flex align-items-center justify-content-center">
                          <div className="d-flex flex-column align-items-stretch w-100 mt-3">
                            <Button
                              variant="warning"
                              className="mb-3 py-2"
                              onClick={handleApplyExtension}
                              disabled={loading}
                            >
                              <ArrowClockwise className="me-2" /> Apply 7-Day Extension
                            </Button>
                            
                            <div className="d-flex justify-content-between">
                              <Button
                                variant="outline-secondary"
                                onClick={handleResetForm}
                                disabled={loading || updateButtonDisabled}
                                className="w-100 me-2"
                              >
                                Reset Changes
                              </Button>
                              
                              <Button
                                variant="primary"
                                onClick={handleUpdateTrialist}
                                disabled={loading || updateButtonDisabled}
                                className="w-100 ms-2"
                              >
                                Update Trial Information
                              </Button>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </Form>
                  </Card.Body>
                </Card>
              </div>
            )}
          </Col>
        </Row>
      </Container>

      {/* Add custom styles for the purchaser input field */}
      <style jsx>{`
        .custom-input-wrapper {
          position: relative;
        }
        .custom-input-wrapper input {
          border-radius: 0.25rem;
          border: 1px solid #ced4da;
          transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
        }
        .custom-input-wrapper input:focus {
          border-color: #86b7fe;
          outline: 0;
          box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
        }
        .custom-input-wrapper input:disabled {
          background-color: #e9ecef;
          opacity: 1;
        }
      `}</style>

      {/* OrientationPrompt for portrait mode */}
      {isPortrait && <OrientationPrompt />}
    </div>
  );
}

export default TrialManagement;